import { ArrowDownTrayIcon, ArrowLeftIcon, ArrowsRightLeftIcon, BellAlertIcon, BookmarkIcon, ChatBubbleOvalLeftEllipsisIcon, CheckIcon, PaperAirplaneIcon, PlayIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { ChatBubbleOvalLeftEllipsisIcon as ChatBubbleOvalLeftEllipsisIconSolid } from "@heroicons/react/24/solid";
import pako from "pako";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useUser } from "../contexts/UserContext";
import { acceptPlaylistShare, addPlaylistItemComment, dismissPlaylistShare, fetchIncomingShares, fetchPlaylistNotifications, fetchPlaylistShareInfo, fetchUserPlaylists, markNotificationsRead, saveUserPlaylist, sharePlaylist } from "../utils/dbutils";

// ── Helpers ───────────────────────────────────────────────────────────────────

function decompressPspl(b64) {
  const binary = atob(b64.trim());
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return pako.inflate(bytes, { to: "string" });
}

/**
 * Parse a decompressed PSPL string into { name, date, events[] }.
 * Line format:
 *   Header: name[0][0]ISO_date
 *   Event:  playerRaw[0]typeGrade[0]videoUrl[0]videoPosition[0]catapultStr[0]subString[0]...
 */
function parsePspl(str) {
  const lines = str.split("\n").filter((l) => l.trim());
  if (!lines.length) return { name: "", date: "", events: [] };

  const headerParts = lines[0].split("[0]");
  const name = headerParts[0] ?? "";
  const date = headerParts[2] ?? "";

  const events = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split("[0]");
    events.push({
      playerRaw: parts[0] ?? "",
      typeGrade: parts[1] ?? "",
      videoUrl: parts[2] ?? "",
      videoPosition: parseFloat(parts[3]) || 0,
      catapultStr: parts[4] ?? "",
      subString: parts[5] ?? "",
    });
  }
  return { name, date, events };
}

export function compressToPspl(str) {
  const compressed = pako.deflate(str);
  let binary = "";
  for (let i = 0; i < compressed.length; i++)
    binary += String.fromCharCode(compressed[i]);
  return btoa(binary);
}

/**
 * Merge multiple playlists into one string.
 * Events are deduplicated by videoPosition + subString.
 */
function mergePsplStrings(name, psplStrings) {
  const header = `${name}[0][0]${new Date().toISOString()}`;
  const seen = new Set();
  const eventLines = [];
  for (const str of psplStrings) {
    const lines = str.split("\n").filter((l) => l.trim());
    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i].split("[0]");
      const videoPosition = parts[3] ?? "";
      const subString = parts[5] ?? "";
      const key = `${videoPosition}|${subString}`;
      if (!seen.has(key)) {
        seen.add(key);
        eventLines.push(lines[i]);
      }
    }
  }
  return [header, ...eventLines].join("\n");
}

export function exportPsplFile(playlist) {
  // Re-embed Firestore comments into segment 6 of each event line before export
  let exportData = playlist.data;
  const firestoreComments = playlist.comments ?? {};
  if (Object.keys(firestoreComments).length > 0) {
    try {
      const str = decompressPspl(playlist.data);
      const lines = str.split("\n");
      let eventIdx = 0;
      const rebuilt = lines.map((line, i) => {
        if (i === 0 || !line.trim()) return line; // header or blank
        const parts = line.split("[0]");
        const idx = eventIdx++;
        const existing = parts[6] ?? "";
        // Collect: already-embedded comments + any Firestore comments not yet embedded
        let embedded = [];
        try { if (existing) embedded = JSON.parse(decodeURIComponent(escape(atob(existing)))); } catch (_) {}
        const stored = firestoreComments[idx] ?? [];
        // Merge by timestamp, deduplicate
        const merged = [...embedded];
        const seenTs = new Set(embedded.map((c) => c.ts));
        for (const c of stored) { if (!seenTs.has(c.ts)) { seenTs.add(c.ts); merged.push(c); } }
        merged.sort((a, b) => a.ts - b.ts);
        parts[6] = merged.length > 0
          ? btoa(unescape(encodeURIComponent(JSON.stringify(merged))))
          : "";
        // Ensure at least 11 segments (indices 0–10)
        while (parts.length < 11) parts.push("");
        return parts.join("[0]");
      });
      exportData = compressToPspl(rebuilt.join("\n"));
    } catch (_) {
      // Fall back to raw data if anything fails
    }
  }
  const blob = new Blob([exportData], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${playlist.name}.pspl`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── VideoPlayer ───────────────────────────────────────────────────────────────
// Uses ReactPlayer. File picker lets user load a local file; URL input for
// http/https streams. seekTo (seconds) is applied via the player ref.

function VideoPlayer({ savedVideoUrl, seekTo }) {
  const playerRef = useRef(null);
  const [videoSrc, setVideoSrc] = useState(savedVideoUrl || null);
  const [playing, setPlaying] = useState(false);
  const readyRef = useRef(false);
  const pendingSeekRef = useRef(null);
  const prevUrlRef = useRef(savedVideoUrl);

  // Reload when the URL changes (different events may have different videos)
  useEffect(() => {
    if (!savedVideoUrl) return;
    if (savedVideoUrl !== prevUrlRef.current) {
      prevUrlRef.current = savedVideoUrl;
      readyRef.current = false;
      setVideoSrc(savedVideoUrl);
    } else if (!videoSrc) {
      setVideoSrc(savedVideoUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedVideoUrl]);

  // Seek whenever seekTo changes
  useEffect(() => {
    if (seekTo == null) return;
    if (readyRef.current && playerRef.current) {
      playerRef.current.seekTo(seekTo, "seconds");
      setPlaying(true);
    } else {
      pendingSeekRef.current = seekTo;
    }
  }, [seekTo]);

  const handleReady = () => {
    readyRef.current = true;
    if (pendingSeekRef.current != null && playerRef.current) {
      playerRef.current.seekTo(pendingSeekRef.current, "seconds");
      setPlaying(true);
      pendingSeekRef.current = null;
    }
  };

  return (
    <div className="w-full h-full bg-black flex items-center justify-center">
      {videoSrc ? (
        <ReactPlayer
          ref={playerRef}
          url={videoSrc}
          playing={playing}
          controls
          width="100%"
          height="100%"
          onReady={handleReady}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
      ) : (
        <p className="text-base-content/60 text-sm">Select an event to play video</p>
      )}
    </div>
  );
}

// ── PlaylistViewer ────────────────────────────────────────────────────────────

export function PlaylistViewer({ playlist, onBack }) {
  const { firebaseUser, currentUser } = useUser();
  const [parsed, setParsed] = useState(null);
  const [parseError, setParseError] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [seekTo, setSeekTo] = useState(null);
  const [savedVideoUrl, setSavedVideoUrl] = useState(null);
  const [leftWidth, setLeftWidth] = useState(280);
  const dragging = useRef(false);
  const containerRef = useRef(null);
  const selectedRowRef = useRef(null);

  // Chat state
  const [localComments, setLocalComments] = useState(() => playlist.comments ?? {});
  const [chatIdx, setChatIdx] = useState(null); // event index whose chat is open
  const [chatDraft, setChatDraft] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const chatInputRef = useRef(null);
  const chatBottomRef = useRef(null);

  const authorName =
    firebaseUser?.displayName || currentUser?.name || firebaseUser?.email || currentUser?.email || "Me";

  useEffect(() => {
    if (chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatIdx, localComments]);

  useEffect(() => {
    if (chatIdx !== null) {
      setChatDraft("");
      setTimeout(() => chatInputRef.current?.focus(), 80);
    }
  }, [chatIdx]);

  const sendComment = async () => {
    if (!chatDraft.trim() || chatIdx == null) return;
    const msg = { text: chatDraft.trim(), author: authorName, ts: Date.now() };
    setChatSending(true);
    const result = await addPlaylistItemComment(playlist.firestoreId, chatIdx, msg, firebaseUser?.uid);
    if (!result.error) {
      setLocalComments((prev) => ({
        ...prev,
        [chatIdx]: [...(prev[chatIdx] ?? []), msg],
      }));
      setChatDraft("");
    }
    setChatSending(false);
    setTimeout(() => chatInputRef.current?.focus(), 50);
  };

  useEffect(() => {
    try {
      const str = decompressPspl(playlist.data);
      const parsedData = parsePspl(str);
      setParsed(parsedData);
      // Pre-load the video URL from the first event so ReactPlayer mounts before any seek
      setSavedVideoUrl(parsedData.events[0]?.videoUrl || null);
    } catch (e) {
      setParseError("Failed to read playlist data: " + e.message);
    }
  }, [playlist]);

  useEffect(() => {
    if (selectedRowRef.current) {
      selectedRowRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIdx]);

  const onMouseDown = useCallback((e) => {
    dragging.current = true;
    e.preventDefault();
  }, []);

  // On a portrait mobile screen, stack the video on top and the event list below.
  const [stackVertical, setStackVertical] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(max-width: 767px) and (orientation: portrait)");
    const update = () => setStackVertical(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const newW = Math.max(160, Math.min(600, e.clientX - rect.left));
      setLeftWidth(newW);
    };
    const onUp = () => { dragging.current = false; };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const handleEventClick = (ev, idx) => {
    setSelectedIdx(idx);
    setSavedVideoUrl(ev.videoUrl || null);
    setSeekTo(ev.videoPosition != null ? ev.videoPosition : null);
  };

  if (parseError) {
    return (
      <div className="flex flex-col m-2 gap-2">
        <button className="btn btn-ghost btn-sm w-fit" onClick={onBack}>
          <ArrowLeftIcon className="w-4 h-4" /> Back
        </button>
        <p className="text-error text-sm">{parseError}</p>
      </div>
    );
  }

  if (!parsed) {
    return (
      <div className="flex flex-col m-2">
        <span className="loading loading-spinner loading-sm" />
      </div>
    );
  }

  return (
    <div className="flex flex-col m-2" style={{ height: "calc(100vh - 6rem)" }}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-2 shrink-0">
        <button className="btn btn-ghost btn-sm" onClick={onBack}>
          <ArrowLeftIcon className="w-4 h-4" /> Back
        </button>
        <h2 className="text-xl font-bold truncate">{parsed.name || playlist.name}</h2>
        <span className="text-xs text-base-content/50">{parsed.events.length} events</span>
        {parsed.date && (
          <span className="text-xs text-base-content/50">{new Date(parsed.date).toLocaleDateString()}</span>
        )}
      </div>

      {/* Two-column body */}
      <div
        ref={containerRef}
        className={`flex flex-1 overflow-hidden border border-base-300 rounded select-none ${
          stackVertical ? "flex-col" : ""
        }`}
      >
        {/* Left — event list */}
        <div
          className={`flex flex-col overflow-hidden border-base-300 ${
            stackVertical
              ? "order-2 flex-1 min-h-0 w-full border-t"
              : "border-r shrink-0"
          }`}
          style={stackVertical ? undefined : { width: leftWidth }}
        >
          <div className="shrink-0 px-2 py-1 border-b border-base-300 bg-base-200 text-xs font-semibold text-base-content/60">
            Events
          </div>
          <ul className="overflow-y-auto flex-1 divide-y divide-base-200 text-xs">
            {parsed.events.map((ev, idx) => {
              const isSelected = selectedIdx === idx;
              const itemComments = localComments[idx] ?? [];
              const hasComments = itemComments.length > 0;
              return (
                <li
                  key={idx}
                  ref={isSelected ? selectedRowRef : null}
                  className={`px-2 py-1.5 cursor-pointer hover:bg-base-200 ${isSelected ? "bg-primary/10 font-semibold" : ""}`}
                  onClick={() => handleEventClick(ev, idx)}
                >
                  {/* Row 1: player + chat icon */}
                  <div className="flex items-center justify-between gap-1">
                    <div className="truncate font-medium flex-1">
                      {ev.playerRaw || "—"}
                    </div>
                    <button
                      className="shrink-0 p-0.5 rounded hover:bg-base-300"
                      title={hasComments ? `${itemComments.length} comment${itemComments.length !== 1 ? "s" : ""}` : "Add comment"}
                      onClick={(e) => { e.stopPropagation(); setChatIdx(idx); }}
                    >
                      {hasComments
                        ? <ChatBubbleOvalLeftEllipsisIconSolid className="w-3.5 h-3.5 text-emerald-500" />
                        : <ChatBubbleOvalLeftEllipsisIcon className="w-3.5 h-3.5 text-base-content/40" />}
                    </button>
                  </div>
                  {/* Row 2: event string (first segment of typeGrade) */}
                  {(() => {
                    const parts = ev.typeGrade ? ev.typeGrade.split(" - ") : [];
                    const eventStr = parts[0] || "—";
                    const subEventParts = parts.slice(1);
                    return (
                      <>
                        <div className={`truncate ${isSelected ? "text-primary" : "text-base-content/60"}`}>
                          {eventStr}
                        </div>
                        {/* Subeventstrings: dash-separated parts + subString */}
                        {(subEventParts.length > 0 || ev.subString || ev.catapultStr) && (
                          <div className="flex flex-col gap-0.5 mt-0.5">
                            {subEventParts.map((part, i) => (
                              <span key={i} className="text-[10px] text-base-content/50 truncate">
                                {part}
                              </span>
                            ))}
                            {ev.subString && (
                              <span className="font-mono text-[10px] text-base-content/50 truncate">
                                {ev.subString}
                              </span>
                            )}
                            {ev.catapultStr && (
                              <span className="font-mono text-[10px] text-base-content/50 truncate">
                                {ev.catapultStr}
                              </span>
                            )}
                          </div>
                        )}
                        {ev.videoPosition > 0 && (
                          <div className="text-[10px] text-base-content/40 font-mono mt-0.5">
                            ▶ {ev.videoPosition.toFixed(1)}s
                          </div>
                        )}
                      </>
                    );
                  })()}
                </li>
              );
            })}
          </ul>
        </div>

        {/* Drag handle */}
        {!stackVertical && (
        <div
          className="w-1 cursor-col-resize hover:bg-primary/40 active:bg-primary/60 bg-base-300 shrink-0 transition-colors"
          onMouseDown={onMouseDown}
        />
        )}

        {/* Right — video player */}
        <div
          className={`flex overflow-hidden ${
            stackVertical ? "order-1 w-full shrink-0 aspect-video" : "flex-1"
          }`}
        >
          <VideoPlayer savedVideoUrl={savedVideoUrl} seekTo={seekTo} />
        </div>
      </div>

      {/* ── Chat modal ── */}
      {chatIdx !== null && (() => {
        const ev = parsed.events[chatIdx];
        const msgs = localComments[chatIdx] ?? [];
        return (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-4">
            <div className="bg-base-100 rounded-xl shadow-2xl w-full max-w-md flex flex-col" style={{ height: "70vh" }}>
              {/* Header */}
              <div className="flex items-start justify-between px-4 py-3 border-b border-base-300 shrink-0">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{ev?.playerRaw || "—"}</p>
                  <p className="text-xs text-base-content/50 truncate">{ev?.typeGrade} {ev?.subString ? `· ${ev.subString}` : ""}</p>
                </div>
                <button className="btn btn-ghost btn-sm btn-circle ml-2 shrink-0" onClick={() => setChatIdx(null)}>
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
                {msgs.length === 0 && (
                  <p className="text-center text-xs text-base-content/50 mt-8">No comments yet. Be the first to add one!</p>
                )}
                {msgs.map((msg, i) => {
                  const isMe = msg.author === authorName;
                  const dt = new Date(msg.ts);
                  const timeStr = dt.toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
                  return (
                    <div key={i} className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${isMe ? "bg-primary text-primary-content rounded-br-sm" : "bg-base-200 text-base-content rounded-bl-sm"}`}>
                        {msg.text}
                      </div>
                      <span className="text-[10px] text-base-content/50 mt-0.5 px-1">
                        {!isMe && <span className="font-medium text-base-content/60 mr-1">{msg.author}</span>}
                        {timeStr}
                      </span>
                    </div>
                  );
                })}
                <div ref={chatBottomRef} />
              </div>

              {/* Input */}
              <div className="shrink-0 px-3 py-2 border-t border-base-300 flex items-center gap-2">
                <input
                  ref={chatInputRef}
                  type="text"
                  className="input input-sm input-bordered flex-1 rounded-full"
                  placeholder="Add a comment…"
                  value={chatDraft}
                  onChange={(e) => setChatDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendComment(); } }}
                  disabled={chatSending}
                />
                <button
                  className="btn btn-sm btn-primary btn-circle"
                  disabled={!chatDraft.trim() || chatSending}
                  onClick={sendComment}
                  title="Send"
                >
                  {chatSending
                    ? <span className="loading loading-spinner loading-xs" />
                    : <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M3.105 2.288a.75.75 0 0 0-.826.95l1.414 4.926A1.5 1.5 0 0 0 5.135 9.25h6.115a.75.75 0 0 1 0 1.5H5.135a1.5 1.5 0 0 0-1.442 1.086l-1.414 4.926a.75.75 0 0 0 .826.95 28.897 28.897 0 0 0 15.293-7.154.75.75 0 0 0 0-1.115A28.897 28.897 0 0 0 3.105 2.288Z"/></svg>}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── PlaylistsPage ─────────────────────────────────────────────────────────────

export default function PlaylistsPage() {
  const { firebaseUser, currentUser } = useUser();
  const uid = firebaseUser?.uid ?? currentUser?.uid ?? null;

  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [viewingPlaylist, setViewingPlaylist] = useState(null);

  // ── Merge state ──
  const [selectedForMerge, setSelectedForMerge] = useState(new Set());
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeName, setMergeName] = useState("");
  const [mergeIntoId, setMergeIntoId] = useState(""); // "" = new, or firestoreId
  const [merging, setMerging] = useState(false);
  const [mergeError, setMergeError] = useState("");

  // ── Share state ──
  const [shareTarget, setShareTarget] = useState([]); // playlists to share
  const [showShareModal, setShowShareModal] = useState(false);
  const [incomingShares, setIncomingShares] = useState([]);
  const [acceptingId, setAcceptingId] = useState(null);
  const [shareInfo, setShareInfo] = useState({ outgoingByPlaylist: {}, incomingByPlaylist: {} });

  // ── Notification state ──
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const notifPanelRef = useRef(null);

  const load = useCallback(async () => {
    if (!uid) { setLoading(false); return; }
    setLoading(true);
    setError("");
    try {
      const [pls, info] = await Promise.all([
        fetchUserPlaylists(uid),
        fetchPlaylistShareInfo(uid, firebaseUser?.email ?? ""),
      ]);
      // Sort newest first
      pls.sort((a, b) => (b.updatedAt ?? b.createdAt ?? 0) - (a.updatedAt ?? a.createdAt ?? 0));
      setPlaylists(pls);
      setShareInfo(info);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [uid, firebaseUser?.email]);

  useEffect(() => { load(); }, [load]);

  // Load incoming shares and notifications whenever the user uid/email is known
  useEffect(() => {
    const email = firebaseUser?.email;
    if (!email) return;
    fetchIncomingShares(email).then(setIncomingShares);
  }, [firebaseUser?.email]);

  useEffect(() => {
    if (!uid) return;
    fetchPlaylistNotifications(uid).then(setNotifications);
  }, [uid]);

  // Close notification panel on outside click
  useEffect(() => {
    const handler = (e) => {
      if (notifPanelRef.current && !notifPanelRef.current.contains(e.target)) {
        setShowNotifPanel(false);
      }
    };
    if (showNotifPanel) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showNotifPanel]);

  const handleOpenNotification = async (notif) => {
    // Mark this notification read
    await markNotificationsRead([notif.notifId]);
    setNotifications((prev) => prev.filter((n) => n.notifId !== notif.notifId));
    setShowNotifPanel(false);
    // Open the playlist
    const pl = playlists.find((p) => p.firestoreId === notif.playlistId);
    if (pl) setViewingPlaylist(pl);
  };

  const handleMarkAllRead = async () => {
    await markNotificationsRead(notifications.map((n) => n.notifId));
    setNotifications([]);
    setShowNotifPanel(false);
  };

  const handleAcceptShare = async (share) => {
    setAcceptingId(share.shareId);
    const result = await acceptPlaylistShare(share.shareId, uid, share);
    setAcceptingId(null);
    if (!result.error) {
      setIncomingShares((prev) => prev.filter((s) => s.shareId !== share.shareId));
      await load();
    }
  };

  const handleDismissShare = async (share) => {
    await dismissPlaylistShare(share.shareId);
    setIncomingShares((prev) => prev.filter((s) => s.shareId !== share.shareId));
  };

  const openShareModal = (pls) => {
    setShareTarget(Array.isArray(pls) ? pls : [pls]);
    setShowShareModal(true);
  };

  const toggleMergeSelect = (firestoreId) => {
    setSelectedForMerge((prev) => {
      const next = new Set(prev);
      if (next.has(firestoreId)) next.delete(firestoreId);
      else next.add(firestoreId);
      return next;
    });
  };

  const openMergeModal = () => {
    setMergeName("");
    setMergeIntoId("");
    setMergeError("");
    setShowMergeModal(true);
  };

  const handleMerge = async () => {
    const selected = playlists.filter((pl) => selectedForMerge.has(pl.firestoreId));
    if (selected.length < 2) return;
    setMerging(true);
    setMergeError("");
    try {
      const psplStrings = selected.map((pl) => decompressPspl(pl.data));
      const targetName = mergeIntoId
        ? (playlists.find((p) => p.firestoreId === mergeIntoId)?.name ?? mergeName.trim())
        : mergeName.trim();
      if (!targetName) { setMergeError("Please enter a name for the merged playlist."); setMerging(false); return; }
      const merged = mergePsplStrings(targetName, psplStrings);
      const b64 = compressToPspl(merged);
      const eventCount = merged.split("\n").filter((l, i) => i > 0 && l.trim()).length;
      const result = await saveUserPlaylist(uid, {
        ...(mergeIntoId ? { firestoreId: mergeIntoId } : {}),
        name: targetName,
        data: b64,
        items: eventCount,
      });
      if (result.error) { setMergeError(result.error); setMerging(false); return; }
      setShowMergeModal(false);
      setSelectedForMerge(new Set());
      await load();
    } catch (e) {
      setMergeError(e.message);
    } finally {
      setMerging(false);
    }
  };

  if (viewingPlaylist) {
    return (
      <PlaylistViewer
        playlist={viewingPlaylist}
        onBack={() => setViewingPlaylist(null)}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 mt-2">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Playlists</h2>
        <div className="flex items-center gap-2">
          {/* Notification bell */}
          <div className="relative" ref={notifPanelRef}>
            <button
              className="btn btn-xs btn-ghost relative"
              onClick={() => setShowNotifPanel((v) => !v)}
              title="Comment notifications"
            >
              <BellAlertIcon className="w-4 h-4" />
              {notifications.length > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-error text-error-content text-[9px] font-bold rounded-full min-w-[14px] h-[14px] flex items-center justify-center px-0.5">
                  {notifications.length > 9 ? "9+" : notifications.length}
                </span>
              )}
            </button>
            {showNotifPanel && (
              <div className="absolute right-0 top-8 z-50 w-80 bg-base-100 border border-base-300 rounded-box shadow-xl flex flex-col">
                <div className="flex items-center justify-between px-3 py-2 border-b border-base-300">
                  <span className="text-xs font-semibold">
                    {notifications.length > 0 ? `${notifications.length} unread` : "No new notifications"}
                  </span>
                  {notifications.length > 0 && (
                    <button className="btn btn-xs btn-ghost" onClick={handleMarkAllRead}>Mark all read</button>
                  )}
                </div>
                <div className="overflow-y-auto max-h-72 flex flex-col divide-y divide-base-200">
                  {notifications.length === 0 ? (
                    <p className="text-xs text-base-content/40 px-3 py-4 text-center">All caught up!</p>
                  ) : (
                    notifications.map((n) => (
                      <button
                        key={n.notifId}
                        className="text-left px-3 py-2 hover:bg-base-200 flex flex-col gap-0.5"
                        onClick={() => handleOpenNotification(n)}
                      >
                        <span className="text-xs font-medium truncate">{n.playlistName}</span>
                        <span className="text-xs text-base-content/60 truncate">
                          <span className="font-semibold text-base-content/80">{n.commentAuthor}</span>: {n.commentText}
                        </span>
                        <span className="text-[10px] text-base-content/40">
                          {new Date(n.createdAt).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          {selectedForMerge.size >= 1 && (
            <button
              className="btn btn-xs btn-info flex items-center gap-1"
              onClick={() => openShareModal(playlists.filter((pl) => selectedForMerge.has(pl.firestoreId)))}
              title="Share selected playlists"
            >
              <PaperAirplaneIcon className="w-4 h-4" />
              Share ({selectedForMerge.size})
            </button>
          )}
          {selectedForMerge.size >= 2 && (
            <button
              className="btn btn-xs btn-secondary flex items-center gap-1"
              onClick={openMergeModal}
              title="Merge selected playlists"
            >
              <ArrowsRightLeftIcon className="w-4 h-4" />
              Merge ({selectedForMerge.size})
            </button>
          )}
          <button className="btn btn-xs btn-ghost" onClick={load} title="Refresh">
            ↺ Refresh
          </button>
        </div>
      </div>

      {!uid && (
        <p className="text-sm text-base-content/50">Sign in to view your playlists.</p>
      )}

      {/* ── Incoming shares ── */}
      {incomingShares.length > 0 && (
        <div className="border border-info/30 bg-info/5 rounded-box p-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-info">
            <BellAlertIcon className="w-4 h-4" />
            {incomingShares.length} incoming playlist share{incomingShares.length !== 1 ? "s" : ""}
          </div>
          {incomingShares.map((share) => (
            <div key={share.shareId} className="flex items-center gap-2 bg-base-100 rounded-lg px-3 py-2 border border-base-300">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{share.playlistName}</p>
                <p className="text-xs text-base-content/50">
                  from <span className="font-medium">{share.fromName || share.fromEmail}</span>
                  {" · "}{new Date(share.createdAt).toLocaleDateString()}
                  {share.playlistItems ? ` · ${share.playlistItems} items` : ""}
                </p>
              </div>
              <button
                className="btn btn-xs btn-success"
                disabled={acceptingId === share.shareId}
                onClick={() => handleAcceptShare(share)}
                title="Accept — add to my playlists"
              >
                {acceptingId === share.shareId
                  ? <span className="loading loading-spinner loading-xs" />
                  : <><CheckIcon className="w-3.5 h-3.5" /> Accept</>}
              </button>
              <button
                className="btn btn-xs btn-ghost text-base-content/40"
                disabled={acceptingId === share.shareId}
                onClick={() => handleDismissShare(share)}
                title="Dismiss"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <span className="loading loading-spinner loading-sm" />
      )}

      {error && (
        <p className="text-sm text-error">{error}</p>
      )}

      {!loading && uid && playlists.length === 0 && (
        <p className="text-sm text-base-content/50">No playlists yet. Create one from the Match Video screen.</p>
      )}

      {playlists.length > 0 && (
        <div className="overflow-x-auto">
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th className="w-8"></th>
                <th>Name</th>
                <th className="text-center">Items</th>
                <th>Updated</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {playlists.map((pl) => {
                const outgoing = shareInfo.outgoingByPlaylist[pl.firestoreId] ?? [];
                const incoming = shareInfo.incomingByPlaylist[pl.firestoreId];
                const isOwner = pl.uid === uid;
                return (
                <tr key={pl.firestoreId} className="hover">
                  <td>
                    <input
                      type="checkbox"
                      className="checkbox checkbox-xs"
                      checked={selectedForMerge.has(pl.firestoreId)}
                      onChange={() => toggleMergeSelect(pl.firestoreId)}
                    />
                  </td>
                  <td className="font-medium">
                    <div>{pl.name}</div>
                    {isOwner && outgoing.length > 0 && (
                      <div
                        className="text-[11px] text-base-content/60 font-normal mt-0.5 flex items-center gap-1 flex-wrap"
                        title={outgoing.map((s) => `${s.toEmail}${s.status && s.status !== "pending" ? ` (${s.status})` : ""}`).join("\n")}
                      >
                        <PaperAirplaneIcon className="w-3 h-3" />
                        <span>Shared with {outgoing.length === 1 ? outgoing[0].toEmail : `${outgoing.length} people`}</span>
                        {outgoing.some((s) => s.status === "pending") && (
                          <span className="badge badge-ghost badge-xs">
                            {outgoing.filter((s) => s.status === "pending").length} pending
                          </span>
                        )}
                      </div>
                    )}
                    {!isOwner && (
                      <div
                        className="text-[11px] text-base-content/60 font-normal mt-0.5 flex items-center gap-1"
                        title={incoming?.fromEmail || pl.sharedBy || ""}
                      >
                        <PaperAirplaneIcon className="w-3 h-3 rotate-180" />
                        <span>
                          Shared by {incoming?.fromName || incoming?.fromEmail || pl.sharedBy || "someone"}
                        </span>
                      </div>
                    )}
                  </td>
                  <td className="text-center text-sm">{pl.items ?? "—"}</td>
                  <td className="text-base-content/50 text-xs">
                    {pl.updatedAt
                      ? new Date(pl.updatedAt).toLocaleString()
                      : "—"}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        className="btn btn-xs btn-ghost btn-square"
                        onClick={() => exportPsplFile(pl)}
                        title="Download as .pspl file"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                      </button>
                      <button
                        className="btn btn-xs btn-ghost btn-square"
                        onClick={() => openShareModal(pl)}
                        title="Share this playlist"
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                      </button>
                      <button
                        className="btn btn-xs btn-ghost btn-square text-primary"
                        onClick={() => setViewingPlaylist(pl)}
                        title="Open playlist viewer"
                      >
                        <PlayIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Share Modal ── */}
      {showShareModal && (
        <ShareModal
          playlists={shareTarget}
          senderUid={uid}
          senderEmail={firebaseUser?.email ?? ""}
          senderName={firebaseUser?.displayName || currentUser?.name || firebaseUser?.email || ""}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* ── Merge Modal ── */}
      {showMergeModal && (
        <div className="modal modal-open z-50">
          <div className="modal-box w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-lg">Merge Playlists</h3>
              <button
                className="btn btn-sm btn-circle btn-ghost"
                onClick={() => setShowMergeModal(false)}
              >
                ✕
              </button>
            </div>

            <p className="text-sm text-base-content/60 mb-3">
              Merging {selectedForMerge.size} playlists. Duplicate events (same
              video position &amp; set info) will be removed.
            </p>

            <ul className="text-xs text-base-content/50 mb-4 list-disc list-inside">
              {playlists
                .filter((pl) => selectedForMerge.has(pl.firestoreId))
                .map((pl) => (
                  <li key={pl.firestoreId}>{pl.name} ({pl.items ?? "?"} items)</li>
                ))}
            </ul>

            <div className="flex flex-col gap-3">
              {/* Target: new or existing */}
              <div>
                <label className="block text-sm font-semibold mb-1">Save result to</label>
                <select
                  className="select select-sm select-bordered w-full"
                  value={mergeIntoId}
                  onChange={(e) => {
                    setMergeIntoId(e.target.value);
                    if (e.target.value) {
                      const pl = playlists.find((p) => p.firestoreId === e.target.value);
                      if (pl) setMergeName(pl.name);
                    } else {
                      setMergeName("");
                    }
                  }}
                >
                  <option value="">New playlist…</option>
                  {playlists
                    .filter((pl) => selectedForMerge.has(pl.firestoreId))
                    .map((pl) => (
                      <option key={pl.firestoreId} value={pl.firestoreId}>
                        {pl.name} (replace)
                      </option>
                    ))}
                </select>
              </div>

              {/* Name (editable for new; locked to selected for existing) */}
              <div>
                <label className="block text-sm font-semibold mb-1">Playlist name</label>
                <input
                  type="text"
                  className="input input-sm input-bordered w-full"
                  placeholder="Merged playlist name…"
                  value={mergeName}
                  disabled={!!mergeIntoId}
                  onChange={(e) => setMergeName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleMerge(); }}
                />
              </div>

              {mergeError && (
                <p className="text-xs text-error">{mergeError}</p>
              )}
            </div>

            <div className="modal-action">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => setShowMergeModal(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary btn-sm"
                disabled={(!mergeName.trim() && !mergeIntoId) || merging}
                onClick={handleMerge}
              >
                {merging ? "Merging…" : "Merge"}
              </button>
            </div>
          </div>
          <div className="modal-backdrop" onClick={() => setShowMergeModal(false)} />
        </div>
      )}
    </div>
  );
}

// ── ShareModal ────────────────────────────────────────────────────────────────

const SHARE_EMAILS_KEY = "psts_share_emails";
const SHARE_LISTS_KEY = "psts_share_email_lists";

function loadSavedLists() {
  try {
    const raw = localStorage.getItem(SHARE_LISTS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") return parsed;
    }
  } catch { /* ignore */ }
  // Migrate the legacy flat list (if any) into a single "Saved addresses" group.
  try {
    const flat = JSON.parse(localStorage.getItem(SHARE_EMAILS_KEY) ?? "[]");
    if (Array.isArray(flat) && flat.length) {
      const migrated = { "Saved addresses": flat };
      localStorage.setItem(SHARE_LISTS_KEY, JSON.stringify(migrated));
      return migrated;
    }
  } catch { /* ignore */ }
  return {};
}

function ShareModal({ playlists, senderUid, senderEmail, senderName, onClose }) {
  const [emails, setEmails] = useState([]);
  const [inputVal, setInputVal] = useState("");
  const [savedLists, setSavedLists] = useState(() => loadSavedLists());
  const [newListName, setNewListName] = useState("");
  const [sharing, setSharing] = useState(false);
  const [shareError, setShareError] = useState("");
  const [done, setDone] = useState(false);
  const inputRef = useRef(null);

  const persistLists = (next) => {
    setSavedLists(next);
    localStorage.setItem(SHARE_LISTS_KEY, JSON.stringify(next));
  };

  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

  const commitInput = () => {
    const raw = inputVal.trim().toLowerCase();
    if (!raw) return;
    const parts = raw.split(/[\s,;]+/).filter(Boolean);
    const valid = parts.filter(isValidEmail);
    if (!valid.length) return;
    setEmails((prev) => [...new Set([...prev, ...valid.filter((e) => !prev.includes(e))])]);
    setInputVal("");
  };

  const removeEmail = (e) => setEmails((prev) => prev.filter((x) => x !== e));

  const saveAsList = () => {
    const name = newListName.trim();
    if (!name || !emails.length) return;
    persistLists({ ...savedLists, [name]: [...emails] });
    setNewListName("");
  };

  const loadList = (name) => {
    const list = savedLists[name];
    if (!list) return;
    setEmails((prev) => [...new Set([...prev, ...list.filter((e) => !prev.includes(e))])]);
  };

  const deleteList = (name) => {
    const next = { ...savedLists };
    delete next[name];
    persistLists(next);
  };

  const handleShare = async () => {
    if (!emails.length || !senderUid) return;
    setSharing(true);
    setShareError("");
    const results = await Promise.all(
      playlists.map((pl) => sharePlaylist(senderUid, senderEmail, senderName, emails, pl))
    );
    setSharing(false);
    const errs = results.filter((r) => r.error);
    if (errs.length) {
      setShareError(errs[0].error);
    } else {
      setDone(true);
      setTimeout(onClose, 1800);
    }
  };

  return (
    <div className="modal modal-open z-50">
      <div className="modal-box w-full max-w-md flex flex-col gap-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <PaperAirplaneIcon className="w-5 h-5 text-info" />
            Share Playlist{playlists.length > 1 ? "s" : ""}
          </h3>
          <button className="btn btn-ghost btn-sm btn-square" onClick={onClose} disabled={sharing}>
            <XMarkIcon className="w-4 h-4" />
          </button>
        </div>

        {/* Playlists being shared */}
        <ul className="text-xs text-base-content/60 list-disc list-inside">
          {playlists.map((pl) => (
            <li key={pl.firestoreId}>{pl.name} ({pl.items ?? "?"} items)</li>
          ))}
        </ul>

        {/* Email input */}
        <div>
          <label className="text-xs font-semibold text-base-content/60 mb-1 block">
            Share with (email addresses)
          </label>
          {/* Tag chips */}
          {emails.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {emails.map((e) => (
                <span key={e} className="badge badge-info badge-sm gap-1">
                  {e}
                  <button className="ml-0.5" onClick={() => removeEmail(e)}>
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              className="input input-sm input-bordered flex-1"
              placeholder="email@example.com, …"
              value={inputVal}
              onChange={(e) => setInputVal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "," || e.key === " ") {
                  e.preventDefault();
                  commitInput();
                }
              }}
              onBlur={commitInput}
              disabled={sharing || done}
            />
            <button
              className="btn btn-sm btn-outline"
              onClick={commitInput}
              disabled={!inputVal.trim() || sharing || done}
            >
              Add
            </button>
          </div>
          <p className="text-xs text-base-content/40 mt-1">Press Enter, comma, or space to add each address.</p>
        </div>

        {/* Saved lists */}
        {Object.keys(savedLists).length > 0 && (
          <div>
            <div className="text-xs font-semibold text-base-content/60 mb-1">Saved lists</div>
            <div className="flex flex-col gap-1">
              {Object.entries(savedLists).map(([name, list]) => (
                <div
                  key={name}
                  className="flex items-center gap-1 bg-base-200 rounded px-2 py-1"
                >
                  <button
                    type="button"
                    className="flex-1 text-left text-sm font-medium truncate hover:text-primary disabled:opacity-50"
                    onClick={() => loadList(name)}
                    disabled={sharing || done}
                    title={`Load ${list.length} address${list.length !== 1 ? "es" : ""}`}
                  >
                    {name}
                    <span className="text-xs text-base-content/50 ml-1">({list.length})</span>
                  </button>
                  <button
                    type="button"
                    className="btn btn-xs btn-ghost btn-square text-error"
                    onClick={() => deleteList(name)}
                    disabled={sharing || done}
                    title="Delete this list"
                  >
                    <XMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Save current as a named list */}
        {emails.length > 0 && !done && (
          <div>
            <div className="text-xs font-semibold text-base-content/60 mb-1">
              Save current recipients as a list
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                className="input input-sm input-bordered flex-1"
                placeholder="List name (e.g. mylist1)"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveAsList(); } }}
                disabled={sharing}
              />
              <button
                type="button"
                className="btn btn-sm btn-outline flex items-center gap-1"
                onClick={saveAsList}
                disabled={!newListName.trim() || sharing}
                title="Save current recipients under this name"
              >
                <BookmarkIcon className="w-4 h-4" />
                Save
              </button>
            </div>
            {newListName.trim() && savedLists[newListName.trim()] && (
              <p className="text-[11px] text-warning mt-1">
                A list called “{newListName.trim()}” already exists — saving will overwrite it.
              </p>
            )}
          </div>
        )}

        {shareError && <p className="text-xs text-error">{shareError}</p>}

        {done && (
          <p className="text-xs text-success flex items-center gap-1">
            <CheckIcon className="w-4 h-4" /> Shared successfully!
          </p>
        )}

        <div className="modal-action mt-0">
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={sharing}>Cancel</button>
          <button
            className="btn btn-info btn-sm"
            disabled={!emails.length || sharing || done}
            onClick={handleShare}
          >
            {sharing
              ? <><span className="loading loading-spinner loading-xs" /> Sharing…</>
              : <><PaperAirplaneIcon className="w-4 h-4" /> Share</>}
          </button>
        </div>
      </div>
      <div className="modal-backdrop" onClick={() => !sharing && onClose()} />
    </div>
  );
}

export { ShareModal };
